import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useGetIdentity, useListContext, useTranslate } from "ra-core";
import { CreateButton } from "@/components/admin/create-button";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { SortButton } from "@/components/admin/sort-button";

import { TopToolbar } from "../layout/TopToolbar";
import { CompanyEmpty } from "./CompanyEmpty";
import { ResetFiltersButton, hasNonDefaultFilters } from "../filters/ResetFiltersButton";

import { CompanyFromUrl } from "./CompanyFromUrl";
import { CompanyListFilter } from "./CompanyListFilter";
import { ImageList } from "./GridList";
import { COMPANY_ROW_HEIGHT, CompanyTable } from "./CompanyTable";
import { Building2, Handshake, LayoutGrid, List as ListIcon } from "lucide-react";
import { EmptyState } from "../misc/EmptyState";
import { COMPANY_FILTER_DEFAULTS } from "../misc/listDefaults";

export const CompanyList = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;
  return (
    <List
      title={false}
      perPage={25}
      sort={{ field: "name", order: "ASC" }}
      filterDefaultValues={COMPANY_FILTER_DEFAULTS}
      actions={<CompanyListActions />}
      pagination={null}
    >
      <CompanyListLayout />
    </List>
  );
};

// LDF: Die Liste füllt genau die Fensterhöhe. Filter links scrollen unabhängig, die Kacheln passen ohne Scrollen
// in den Bereich, und die Seitenauswahl steht immer sichtbar darunter. Kacheln pro Seite ergeben sich aus der Fenstergröße.
const CARD_MIN_WIDTH = 180;
const CARD_HEIGHT = 150;
const GAP = 8;

const CompanyListLayout = () => {
  const { data, isPending, filterValues, perPage, setPerPage } = useListContext();
  const hasFilters = hasNonDefaultFilters(filterValues, COMPANY_FILTER_DEFAULTS);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [view, setView] = useState<"kacheln" | "liste">(() => {
    try {
      return localStorage.getItem("ldf.companies.view") === "liste" ? "liste" : "kacheln";
    } catch {
      return "kacheln";
    }
  });
  const switchView = (next: "kacheln" | "liste") => {
    setView(next);
    try {
      localStorage.setItem("ldf.companies.view", next);
    } catch {
      // Speicher nicht verfügbar
    }
  };

  // Höhe bis zum Fensterende
  useLayoutEffect(() => {
    const update = () => {
      const top = wrapperRef.current?.getBoundingClientRect().top ?? 0;
      setHeight(Math.max(320, window.innerHeight - top - 12));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [isPending]);

  // Kacheln pro Seite = Spalten × Zeilen, die in den sichtbaren Bereich passen
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout>;
    const measure = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const fit =
          view === "liste"
            ? Math.max(5, Math.floor((el.clientHeight - 34) / COMPANY_ROW_HEIGHT))
            : Math.max(1, Math.floor((el.clientWidth + GAP) / (CARD_MIN_WIDTH + GAP))) *
              Math.max(1, Math.floor((el.clientHeight + GAP) / (CARD_HEIGHT + GAP)));
        if (fit !== perPage) setPerPage(fit);
      }, 150);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [perPage, setPerPage, height, view]);

  if (isPending && !data) return null;
  if (!isPending && !data?.length && !hasFilters) return <CompanyEmpty />;

  return (
    <div ref={wrapperRef} className="w-full flex flex-row gap-8" style={height ? { height } : undefined}>
      <div className="h-full shrink-0 overflow-y-auto pr-1">
        <CompanyListFilter />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div ref={gridRef} className="min-h-0 flex-1 overflow-hidden">
          {!isPending && !data?.length ? (
            <EmptyState Icon={Building2} title="Keine Firmen für diese Filter">
              <div className="w-56">
                <ResetFiltersButton defaults={COMPANY_FILTER_DEFAULTS} />
              </div>
            </EmptyState>
          ) : view === "liste" ? (
            <CompanyTable />
          ) : (
            <ImageList />
          )}
        </div>
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border pt-1">
          <div className="flex gap-1 rounded-full bg-muted p-1">
            {(
              [
                ["kacheln", "Kacheln", LayoutGrid],
                ["liste", "Liste", ListIcon],
              ] as const
            ).map(([value, label, Icon]) => (
              <button
                key={value}
                type="button"
                onClick={() => switchView(value)}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs cursor-pointer ${view === value ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
          <ListPagination rowsPerPageOptions={[]} />
        </div>
      </div>
    </div>
  );
};

const CompanyListActions = () => {
  const translate = useTranslate();
  return (
    <TopToolbar>
      <CustomersButton />
      <CompanyFromUrl />
      <SortButton fields={["name", "created_at", "nb_contacts"]} />
      <ExportButton />
      <CreateButton
        label={translate("resources.companies.action.new", {
          _: "New Company",
        })}
      />
    </TopToolbar>
  );
};

// LDF: Schnellfilter „Kunden“ – alle Firmen, die als Auftraggeber oder Endkunde in einem Auftrag stehen
// (Anfrage, Angebot, Auftrag, Rechnung – auch verlorene).
const CustomersButton = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const active = filterValues?.is_customer === true;
  const toggle = () => {
    const next = { ...filterValues };
    if (active) delete next.is_customer;
    else next.is_customer = true;
    setFilters(next, displayedFilters);
  };
  return (
    <button
      type="button"
      onClick={toggle}
      title="Alle Firmen mit Anfrage, Angebot, Auftrag oder Rechnung"
      className={`inline-flex h-9 items-center gap-1 rounded-full px-3 text-sm cursor-pointer ${active ? "bg-blue-600 text-white" : "border border-border text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"}`}
    >
      <Handshake className="h-4 w-4" /> Kunden
    </button>
  );
};
